/*
 * Copyright (c) CIRROM 2024.
 */

import { API } from "./api";

export interface SyncDetails {
  sync_group_id?: number;
  system_a_id: number;
  system_a_name: string;
  system_b_id: number;
  system_b_name: string;
  total_contacts_in_scope: number;
  total_contacts_matched: number;
  total_contacts_proposed: number;
  total_contacts_unmatched: number;
  total_contacts_with_unmatched_fields: number;
}

export interface SyncRecord {
  url: string;
  is_create_new?: boolean;
  crm_contact_id?: number | null;
  company: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  job_title: string;
  field_value: string;
  other_system_id: string;
}

/**
 * A single attribute the adaptor manifest publishes for the group's source object,
 * carrying the live source value plus the tenant's resolved CRM target per write mode.
 * `value` is heterogeneous (string / number / pre-formatted date string / list / null) and
 * `targets[mode]` is null when the tenant has not mapped that attribute for that mode.
 */
export interface SyncSourceAttribute {
  source_key: string;
  label: string;
  /** "raw" | "derived" in practice; kept wide, like SourceFieldMappingAttribute.kind. */
  kind: string;
  /** "publish" | "enrich" in practice; string[] so orderModes() needs no cast. */
  modes: string[];
  value: string | number | boolean | string[] | number[] | null;
  /** Keyed by the same modes as `modes`; null means "not mapped for this mode". */
  targets: Record<string, string | null>;
}

export interface SyncUnmatchedRecord {
  url: string;
  company: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  job_title: string;
  crm_entity_id: string;
  cirrom_entity_id: number;
}

export interface SyncGroup {
  sync_group_id: number;
  match_source: string;
  system_a_name: string;
  system_b_name: string;
  system_a_record: SyncRecord;
  system_b_record: SyncRecord;
  /** Manifest object key ("article" / "subscriber"), or null when nothing is published. */
  source_object?: string | null;
  /** Absent for data types with no manifest object (contacts, accounts, ...). */
  source_attributes?: SyncSourceAttribute[];
}

export interface SyncLinkageProposal {
  proposal_id: number;
  match_source: string;
  system_b_record: SyncRecord | null;
  is_create_new?: boolean;
  entity_matching_status?: string;
}

export interface SyncLinkageProposalGroup {
  matching_id: number;
  system_a_name: string;
  system_b_name: string;
  system_a_record: SyncRecord;
  rows: SyncLinkageProposal[];
  /** Group-level: shared by every proposal row in the group. */
  source_object?: string | null;
  source_attributes?: SyncSourceAttribute[];
}

export interface SyncLinkageFilters {
  emailsAreTheSameFilter: boolean;
  firstNamesAreTheSameFilter: boolean;
  lastNamesAreTheSameFilter: boolean;
  jobTitlesAreTheSameFilter: boolean;
  companiesAreSimilarFilter: boolean;
  oneHasNoEmailFilter: boolean;
  oneHasNoCompanyFilter: boolean;
  includeCrmFieldProposals: boolean;
  includeCirromProposals: boolean;
  bothAdditionalAttributesBlank: boolean;
  hasCrmCandidateFilter: boolean;
  noCrmCandidateFilter: boolean;
}

export interface SyncFieldFilters {
  casingDifferenceFilter: boolean;
  oneIsBlankFilter: boolean;
  haveCompaniesFilter: boolean;
  companiesMatchFilter: boolean;
}

export interface SyncFieldMatching {
  sync_groups: SyncGroup[];
  sync_fields: string[];
}

export interface SyncChange {
  field: string;
  old_value: string;
  new_value: string;
  sync_group_id?: number;
}

export interface SyncProjectOption {
  name: string;
  value: string;
}

export interface SyncProject {
  sync_group_id?: number;
  sync_system_id?: number;
  name: string;
  crm_system_id: number;
  sync_system_type: string;
  options: SyncProjectOption[];
}

export interface EventSourceMetadata {
  event_start?: string | null;
  event_end?: string | null;
  event_archive?: string | null;
  event_timezone?: string | null;
  source_event_status?: string | null;
  metadata_refreshed_at?: string | null;
  lifecycle_phase?:
    | "pre_event"
    | "live"
    | "post_event"
    | "archived"
    | "cancelled"
    | "unknown";
}

export interface EventSyncDashboardEvent extends EventSourceMetadata {
  registered_count?: number | null;
  declined_count?: number | null;
  walk_in_count?: number | null;
  impact?: {
    blank_fields_populated: number;
    job_titles_updated: number;
    contacts_resolved: number;
  } | null;
  suppression_issues_count?: number | null;
  suppression_coverage?: Array<{
    stream: string;
    status: string;
    kind: string;
    completed_at: string | null;
    covered_from: string | null;
    covered_through: string | null;
  }>;
  invited_count?: number | null;
  opened_count?: number | null;
  funnel?: {
    invited: number;
    opened: number;
    registered: number;
    attended: number;
    registration_rate: number | null;
    attendance_rate: number | null;
  } | null;
  email_coverage?: {
    status: string;
    attempted_at: string;
    completed_at: string | null;
    covered_from: string | null;
    latest_window_from?: string | null;
    covered_through: string | null;
    complete_history: boolean;
  } | null;
  attended_count?: number | null;
  config_id?: number;
  event_id: string;
  event_name: string;
  crm_list_id?: string;
  crm_list_name?: string;
  sync_contacts: number;
  unmanaged_event_contacts: number;
  contact_diffs: number;
}

export interface EventSyncDashboard {
  sync_group_id: number;
  crm_system_id: number;
  crm_system_name: string;
  event_system_id: number;
  event_system_name: string;
  sync_contacts: number;
  unmanaged_event_contacts: number;
  contact_diffs: number;
  events: EventSyncDashboardEvent[];
}

export interface EventContactReviewCandidate {
  proposal_id: number;
  crm_contact_id: string;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  company: string | null;
}

export interface EventContactReviewRow {
  event_contact_id: string;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  candidates: EventContactReviewCandidate[];
  event_ids: string[];
  reason: "conflicting_links" | "possible_match" | "no_crm_match";
  current_crm_contact_id: string | null;
  conflicting_contacts: {
    event_contact_id: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string | null;
  }[];
}

export interface EventSyncCrmListOption {
  list_id: string;
  list_name: string;
}

export interface EventSyncEventOption {
  event_id: string;
  event_name: string;
}

export interface EventSyncConfigOptions {
  crm_lists: EventSyncCrmListOption[];
  events: EventSyncEventOption[];
  source_activity_types: EventSyncSourceActivityTypeOption[];
  target_activity_types: EventSyncTargetActivityTypeOption[];
  activity_mappings: EventSyncActivityTypeMapping[];
}

export interface EventSyncSourceActivityTypeOption {
  value: string;
  label: string;
  family: string;
}

export interface EventSyncTargetActivityTypeOption {
  activityTypeId: string;
  name: string;
  group?: string;
  isSearchable?: boolean;
}

export interface EventSyncActivityTypeMapping {
  mapping_id?: number;
  source_activity_type: string;
  target_activity_type_id: string;
  target_activity_type_name: string;
}

export interface EventSyncFieldOption {
  value: string;
  label: string;
}

export interface EventSyncFieldConfig {
  field_config_id?: number | null;
  crm_field: string;
  event_field: string;
}

export interface EventSyncFieldConfigResponse {
  fields: EventSyncFieldConfig[];
  available_fields: EventSyncFieldOption[];
}

export interface EventSyncEventConfig {
  sync_group_id: number;
  crm_list_id: string;
  crm_list_name: string;
  event_id: string;
  event_name: string;
}

export type EventMappingValueType = "option" | "text" | "boolean";
export interface EventSyncCrmChoice {
  crm_value_id: string;
  crm_value_name: string;
}
export interface EventSyncCrmField {
  crm_attribute_id: string;
  crm_attribute_name: string;
  crm_data_type: string;
  crm_attribute_type: EventMappingValueType | null;
  multi_value: boolean | null;
  supported: boolean;
  unsupported_reason: string | null;
  values: EventSyncCrmChoice[];
  values_available: boolean;
}
export interface EventSyncCrmFieldMetadata {
  crm_list_id: string;
  fields: EventSyncCrmField[];
}
export interface EventSyncValueMapping {
  source_state: string;
  source_state_supported?: boolean;
  target_value_kind: EventMappingValueType | null;
  crm_value_id: string | null;
  crm_value_name: string | null;
  crm_value_literal: string | null;
}
export interface EventSyncStateOption {
  key: string;
  label: string;
}
export interface EventSyncStateOptions {
  registration: EventSyncStateOption[];
  attendance: EventSyncStateOption[];
}
export interface EventSyncKindMapping {
  crm_attribute_id: string | null;
  crm_attribute_name: string | null;
  crm_attribute_type: EventMappingValueType | null;
  values: EventSyncValueMapping[];
  configured: boolean;
}
export interface EventSyncEventMapping extends EventSourceMetadata {
  config_id: number;
  sync_group_id: number;
  event_id: string;
  event_name: string;
  crm_list_id: string;
  crm_list_name: string;

  registration: EventSyncKindMapping;
  attendance: EventSyncKindMapping;
}
export type EventSyncKindMappingSave = Pick<
  EventSyncKindMapping,
  "crm_attribute_id"
> & {
  values: Array<
    Pick<
      EventSyncValueMapping,
      | "source_state"
      | "target_value_kind"
      | "crm_value_id"
      | "crm_value_literal"
    >
  >;
};
export interface EventSyncEventMappingSave {
  sync_group_id: number;
  config_id: number;
  crm_list_id: string;
  registration: EventSyncKindMappingSave;
  attendance: EventSyncKindMappingSave;
}
export interface EventSyncEventMappingCreate {
  sync_group_id: number;
  crm_list_id: string;
  crm_list_name: string;
  event_id: string;
  event_name: string;
  registration: EventSyncKindMappingSave;
  attendance: EventSyncKindMappingSave;
}

export interface SourceFieldMappingAttribute {
  source_key: string;
  label: string;
  kind: string;
  modes: string[];
  default_target: string;
}

export interface SourceFieldMappingManifestObject {
  source_object: string;
  entity: string;
  modes: string[];
  attributes: SourceFieldMappingAttribute[];
}

export interface SourceFieldMappingSavedField {
  source_key: string;
  target_field: string;
}

export interface SourceFieldMappingObject {
  source_object: string;
  write_mode: string;
  crm_object: string;
  fields: SourceFieldMappingSavedField[];
}

export interface SourceFieldMappingResponse {
  objects: SourceFieldMappingObject[];
  manifest: SourceFieldMappingManifestObject[];
}

export const getSourceFieldMapping = async (
  syncGroupId: number
): Promise<SourceFieldMappingResponse> => {
  const response = await API.get("/get-source-field-mapping", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const saveSourceFieldMapping = async (
  syncGroupId: number,
  objects: SourceFieldMappingObject[]
): Promise<SourceFieldMappingResponse> => {
  const response = await API.post("/save-source-field-mapping", {
    sync_group_id: syncGroupId,
    objects,
  });
  return response.data;
};

export const getSyncResultsForSystemId = async (
  systemId: number,
  dataType: string,
  syncGroupId?: number
): Promise<SyncDetails> => {
  const response = await API.get("/get-sync-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
      sync_group_id: syncGroupId,
    },
  });
  return response.data;
};

export const getEventSyncDashboard = async (
  syncGroupId: number
): Promise<EventSyncDashboard> => {
  const response = await API.get("/get-event-sync-dashboard", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const getEventContactReview = async (
  syncGroupId: number
): Promise<EventContactReviewRow[]> => {
  const response = await API.get("/get-event-contact-review", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export interface EventEmailReviewRow extends EventContactReviewRow {
  crm_primary_email: string | null;
}
export const getEventEmailReview = async (
  syncGroupId: number
): Promise<EventEmailReviewRow[]> => {
  const response = await API.get("/get-event-email-review", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};
export const resolveEventEmail = async (
  syncGroupId: number,
  contact: EventEmailReviewRow,
  keep: "crm" | "cvent"
): Promise<void> => {
  await API.post("/resolve-event-email", {
    sync_group_id: syncGroupId,
    event_contact_id: contact.event_contact_id,
    crm_contact_id: contact.current_crm_contact_id,
    crm_email: contact.crm_primary_email,
    cvent_email: contact.primary_email,
    keep,
  });
};

export type EventCrmContact = Omit<EventContactReviewCandidate, "proposal_id">;

export const searchEventCrmContacts = async (
  syncGroupId: number,
  eventContactId: string,
  query: string
): Promise<{ contacts: EventCrmContact[]; has_more: boolean }> => {
  const response = await API.get("/search-event-crm-contacts", {
    params: {
      sync_group_id: syncGroupId,
      event_contact_id: eventContactId,
      query,
    },
  });
  return response.data;
};

export const linkEventCrmContact = async (
  syncGroupId: number,
  eventContactId: string,
  crmContactId: string
): Promise<void> => {
  await API.post("/link-event-crm-contact", {
    sync_group_id: syncGroupId,
    event_contact_id: eventContactId,
    crm_contact_id: crmContactId,
  });
};

export const approveEventContactProposal = async (
  syncGroupId: number,
  proposalId: number
): Promise<void> => {
  await API.post("/approve-event-contact-proposal", {
    sync_group_id: syncGroupId,
    proposal_id: proposalId,
  });
};

export const deleteEventSyncEvent = async (
  syncGroupId: number,
  eventId: string
): Promise<{ sync_group_id: number; event_id: string; deleted: number }> => {
  const response = await API.delete("/delete-event-sync-event", {
    params: { sync_group_id: syncGroupId, event_id: eventId },
  });
  return response.data;
};

export const getEventSyncConfigOptions = async (
  syncGroupId: number
): Promise<EventSyncConfigOptions> => {
  const response = await API.get("/get-event-sync-config-options", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const getEventSyncEventMapping = async (
  syncGroupId: number,
  configId: number
): Promise<EventSyncEventMapping> => {
  const response = await API.get("/get-event-sync-event-mapping", {
    params: { sync_group_id: syncGroupId, config_id: configId },
  });
  return response.data;
};

export const getEventSyncStateOptions = async (
  syncGroupId: number
): Promise<EventSyncStateOptions> => {
  const response = await API.get("/get-event-sync-state-options", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const getEventSyncCrmFieldMetadata = async (
  syncGroupId: number,
  crmListId: string
): Promise<EventSyncCrmFieldMetadata> => {
  const response = await API.get("/get-event-sync-crm-field-metadata", {
    params: { sync_group_id: syncGroupId, crm_list_id: crmListId },
  });
  return response.data;
};

export const saveEventSyncEventMapping = async (
  request: EventSyncEventMappingSave
): Promise<EventSyncEventMapping> => {
  const response = await API.put("/save-event-sync-event-mapping", request);
  return response.data;
};

export const createEventSyncEventMapping = async (
  request: EventSyncEventMappingCreate
): Promise<EventSyncEventMapping> => {
  const response = await API.post("/create-event-sync-event-mapping", request);
  return response.data;
};

export const getEventSyncFieldConfig = async (
  syncGroupId: number
): Promise<EventSyncFieldConfigResponse> => {
  const response = await API.get("/get-event-sync-field-config", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const saveEventSyncFieldConfig = async (
  syncGroupId: number,
  fields: EventSyncFieldConfig[]
): Promise<EventSyncFieldConfigResponse> => {
  const response = await API.post("/save-event-sync-field-config", {
    sync_group_id: syncGroupId,
    fields,
  });
  return response.data;
};

export const upsertEventSyncEvent = async (
  config: EventSyncEventConfig
): Promise<EventSyncEventConfig & { id: number }> => {
  const response = await API.put("/upsert-event-sync-event", config);
  return response.data;
};

export const saveEventSyncActivityTypeConfig = async (
  syncGroupId: number,
  mappings: EventSyncActivityTypeMapping[]
): Promise<{
  sync_group_id: number;
  activity_mappings: EventSyncActivityTypeMapping[];
}> => {
  const response = await API.post("/save-event-sync-activity-type-config", {
    sync_group_id: syncGroupId,
    mappings,
  });
  return response.data;
};

export const rerunSyncGroup = async (
  syncGroupId: number
): Promise<{ sync_group_id: number; status: string }> => {
  const response = await API.post("/rerun-sync-group", {
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const upsertSyncProject = async (
  syncProject: SyncProject
): Promise<SyncProject> => {
  const response = await API.put("/upsert-sync-project", syncProject);
  return response.data;
};

export const getSyncLinkageProposals = async (
  systemId: number,
  dataType: string,
  filters: SyncLinkageFilters,
  singleOptionFilter: boolean,
  statusFilter: string = "proposed",
  syncGroupId?: number
): Promise<SyncLinkageProposalGroup[]> => {
  const response = await API.get("/get-sync-linkage-proposals-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
      emails_are_the_same: filters.emailsAreTheSameFilter,
      first_names_are_the_same: filters.firstNamesAreTheSameFilter,
      last_names_are_the_same: filters.lastNamesAreTheSameFilter,
      job_titles_are_the_same: filters.jobTitlesAreTheSameFilter,
      companies_are_similar: filters.companiesAreSimilarFilter,
      one_has_no_email: filters.oneHasNoEmailFilter,
      one_has_no_company: filters.oneHasNoCompanyFilter,
      include_crm_field_proposals: filters.includeCrmFieldProposals,
      include_cirrom_proposals: filters.includeCirromProposals,
      both_additional_attributes_blank: filters.bothAdditionalAttributesBlank,
      single_options_filter: singleOptionFilter,
      status_filter: statusFilter,
      sync_group_id: syncGroupId,
    },
  });
  return response.data;
};

export const getSyncUnmatchedContacts = async (
  systemId: number,
  dataType: string
): Promise<SyncUnmatchedRecord[]> => {
  const response = await API.get("/get-sync-unmatched-contacts-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
    },
  });
  return response.data;
};

export const searchForContactsFromOtherSystem = async (
  systemId: number,
  firstName: string,
  lastName: string,
  email: string
): Promise<SyncUnmatchedRecord[]> => {
  const response = await API.get("/search-contacts-for-other-system", {
    params: {
      system_id: systemId,
      first_name: firstName,
      last_name: lastName,
      email: email,
    },
  });
  return response.data;
};

export const getSyncFieldMatching = async (
  systemId: number,
  dataType: string,
  fieldFilter: string,
  filter: SyncFieldFilters
): Promise<SyncFieldMatching> => {
  const response = await API.get("/get-sync-field-matching-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
      field_filter: fieldFilter,
      casing_difference_filter: filter.casingDifferenceFilter,
      one_is_blank_filter: filter.oneIsBlankFilter,
      have_companies_filter: filter.haveCompaniesFilter,
      companies_match_filter: filter.companiesMatchFilter,
    },
  });
  return response.data;
};

export const approveSyncLinkageProposal = async (
  systemId: number,
  proposalId: number,
  syncGroupId?: number
): Promise<any> => {
  const response = await API.post("/approve-sync-linkage-proposal", {
    system_id: systemId,
    proposal_id: proposalId,
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const rejectSyncLinkageProposal = async (
  systemId: number,
  proposalId: number,
  syncGroupId?: number
): Promise<any> => {
  const response = await API.post("/reject-sync-linkage-proposal", {
    system_id: systemId,
    proposal_id: proposalId,
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const deleteSyncLinkage = async (
  systemId: number,
  linkId: number,
  syncGroupId?: number
): Promise<SyncGroup[]> => {
  const response = await API.post("/delete-sync-linkage", {
    system_id: systemId,
    linkage_id: linkId,
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const forceMatchSyncLinkage = async (
  systemId: number,
  system_a_cirrom_id: number,
  system_b_cirrom_id: number,
  syncGroupId?: number
): Promise<SyncGroup[]> => {
  const response = await API.post("/force-match-sync-linkage", {
    system_id: systemId,
    system_a_cirrom_id: system_a_cirrom_id,
    system_b_cirrom_id: system_b_cirrom_id,
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const approveSyncLinkageProposals = async (
  systemId: number,
  proposalIds: number[],
  syncGroupId?: number
): Promise<any> => {
  const response = await API.post("/approve-sync-linkage-proposals", {
    system_id: systemId,
    proposals: proposalIds,
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const rejectSyncLinkageProposals = async (
  systemId: number,
  proposalIds: number[],
  syncGroupId?: number
): Promise<any> => {
  const response = await API.post("/reject-sync-linkage-proposals", {
    system_id: systemId,
    proposals: proposalIds,
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const approveSyncFields = async (
  systemId: number,
  syncGroupId: number,
  systemAChanges: SyncChange[],
  systemBChanges: SyncChange[]
): Promise<SyncGroup[]> => {
  const response = await API.post("/approve-sync-field-changes", {
    system_id: systemId,
    sync_group_id: syncGroupId,
    system_a_changes: systemAChanges,
    system_b_changes: systemBChanges,
  });
  return response.data;
};

export const approveMultipleSyncFields = async (
  systemId: number,
  systemAChanges: SyncChange[],
  systemBChanges: SyncChange[]
): Promise<SyncGroup[]> => {
  const response = await API.post("/approve-multiple-sync-field-changes", {
    system_id: systemId,
    system_a_changes: systemAChanges,
    system_b_changes: systemBChanges,
  });
  return response.data;
};

export const getSyncFieldChangeMetrics = async (
  systemId: number,
  dataType: string
) => {
  const response = await API.get("/get-field-sync-change-metrics-for-system", {
    params: {
      integrated_system_id: systemId,
      data_type: dataType,
    },
  });
  return response.data;
};

export interface SyncIntegratedSystem {
  sync_group_id: number;
  system_a_id: number;
  system_a_name: string;
  system_a_type: string;
  system_b_id: number;
  system_b_name: string;
  system_b_type: string;
  matched_count: number;
  sync_data_type: "CRM" | "EVENTS" | "CONTENT" | "MAILBOX" | "ERM_PLATFORM";
}

export type SyncSlotDataType = "CRM" | "CONTENT" | "MAILBOX";

export interface SyncIntegrationOptionSystem {
  id: number;
  name: string;
  system_type: string;
}

export interface SyncIntegrationPairing {
  id: string;
  label: string;
  slot_a_data_type: SyncSlotDataType;
  slot_b_data_type: SyncSlotDataType;
  runnable: boolean;
  slot_a_available_system_ids: number[];
  slot_b_available_system_ids: number[];
}

export interface SyncIntegrationOptions {
  pairings: SyncIntegrationPairing[];
  systems_by_data_type: Record<SyncSlotDataType, SyncIntegrationOptionSystem[]>;
}

export const getAllSyncIntegratedSystems = async (): Promise<
  SyncIntegratedSystem[]
> => {
  const response = await API.get("/get-all-sync-integrated-systems");
  return response.data;
};

export const getAllSyncIntegrationOptions =
  async (): Promise<SyncIntegrationOptions> => {
    const response = await API.get("/get-all-sync-integration-options");
    return response.data;
  };

export const syncIntegratedSystems = async (
  systemASystemId: number,
  systemBSystemId: number
): Promise<{ sync_group_id: number }> => {
  const response = await API.put("/sync-integrated-systems", {
    system_a_system_id: systemASystemId,
    system_b_system_id: systemBSystemId,
  });
  return response.data;
};

export const getSyncLinkageChangeMetrics = async (
  systemId: number,
  dataType: string
) => {
  const response = await API.get(
    "/get-linkage-sync-change-metrics-for-system",
    {
      params: {
        integrated_system_id: systemId,
        data_type: dataType,
      },
    }
  );
  return response.data;
};

export interface SyncMatchingCount {
  data_entity_type: string | null;
  status: string | null;
  count: number;
}

export interface SyncProcessProgress {
  sync_batch_id: number;
  sync_batch_status: string | null;
  process_name: string;
  status: string | null;
  failure_count: number;
  start_time: string | null;
  last_update_time: string | null;
}

export interface SyncGroupSummary {
  sync_group_id: number;
  system_a_name: string;
  system_b_name: string;
  rerun_now: boolean;
  matching_counts: SyncMatchingCount[];
  progress: SyncProcessProgress[];
}

export const getSyncGroupSummary = async (
  integratedSystemId: number
): Promise<SyncGroupSummary[]> => {
  const response = await API.get("/get-sync-group-summary", {
    params: { integrated_system_id: integratedSystemId },
  });
  return response.data;
};

export interface EventContactFieldDifference {
  field: string;
  label: string;
  crm_value: string | null;
  event_value: string | null;
}
export interface EventContactDiffRow extends EventContactReviewRow {
  field_differences: EventContactFieldDifference[];
}
export const getEventContactDiffs = async (
  syncGroupId: number
): Promise<EventContactDiffRow[]> => {
  const response = await API.get("/get-event-contact-diffs", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};
export const resolveEventContactField = async (
  syncGroupId: number,
  contact: EventContactDiffRow,
  difference: EventContactFieldDifference,
  keep: "crm" | "cvent",
  eventConfigId?: number
): Promise<void> => {
  await API.post("/resolve-event-contact-field", {
    sync_group_id: syncGroupId,
    event_config_id: eventConfigId,
    event_contact_id: contact.event_contact_id,
    crm_contact_id: contact.current_crm_contact_id,
    field: difference.field,
    crm_value: difference.crm_value,
    event_value: difference.event_value,
    keep,
  });
};

export interface EventSuppressionIssue {
  id: number;
  contact_id: string;
  kind: string;
  state: string;
  source_system: string;
  first_observed_at: string;
  last_observed_at: string;
  evidence: Array<{
    scope: string;
    status: string;
    classification: string | null;
    reason: string | null;
    source_occurred_at: string | null;
    observed_at: string;
  }>;
}
export const getEventSuppressionEvidence = async (
  syncGroupId: number,
  configId: number,
  offset = 0
): Promise<{ issues: EventSuppressionIssue[]; next_offset: number | null }> => {
  const response = await API.get("/get-event-suppression-evidence", {
    params: { sync_group_id: syncGroupId, config_id: configId, offset },
  });
  return response.data;
};
